import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import { useFetchMatterById } from '../../hooks/useFetchMatterById';
import { useFetchMatterParameters } from '../../hooks/useFetchMatterParameters';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { usePreliminaryInvoiceCreation } from '../../hooks/usePreliminaryInvoiceCreation';
import { BILLABLE_MATTER_QUERY, isInvoicingPeriodInUse } from '../../constants/invoice';
import { formatDateRange } from '../../constants/utilityFunctions';
import { ParameterEntityType } from '../../interface/common';
import { PreliminaryInvoiceCreationType } from '../../interface/invoice';
import { InvoicingPeriod } from '../../interface/parameters';
import ClosableModal from '../closableModal/closableModal';
import CustomTabs from '../customTabs/customTabs';
import {
  PreliminaryInvoicePeriodicalSkeleton,
  PreliminaryInvoiceUntilDateSkeleton,
} from './preliminaryInvoiceFormSkeletons';
import PreliminaryInvoicePeriodicalForm from './preliminaryInvoicePeriodicalForm';
import PreliminaryInvoiceUntilDateForm from './preliminaryInvoiceUntilDateForm';
import { CreationTabs } from './preliminaryInvoiceCreation.styled';

interface CreatePreliminaryInvoiceModalProps {
  matterId: string;
  onClose: () => void;
}

const CreatePreliminaryInvoiceModal = ({
  matterId,
  onClose,
}: CreatePreliminaryInvoiceModalProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [selectedTab, setSelectedTab] = useState<string>();
  const { data: matter, isLoading } = useFetchMatterById({
    id: matterId,
    ...BILLABLE_MATTER_QUERY,
  });
  const { data: matterParameters, isLoading: isLoadingMatterParameters } = useFetchMatterParameters(
    {
      matterId,
      enabled: true,
      parametersToGet: [ParameterEntityType.INVOICING_PERIOD],
    },
  );
  const { data: invoiceParameters, isLoading: isLoadingInvoiceParameters } = useFetchUserParameters(
    {
      parametersToGet: ParameterEntityType.INVOICE,
      enabled: !!user?.id,
    },
  );
  const { requestCreation, CreationModals } = usePreliminaryInvoiceCreation(matter);
  const isEntryDateLocked =
    !!invoiceParameters?.parameters?.invoiceParameters?.useInvoiceDateAsEntryDate;
  const periodType: InvoicingPeriod | null | undefined =
    matterParameters?.parameters?.invoicingPeriodParameters?.invoicingPeriod;
  const invoicingPeriod = formatDateRange(matter?.periodStartDate, matter?.periodEndDate);
  const isLoadingPeriodical = isLoading || isLoadingMatterParameters;
  const isLoadingUntilDate = isLoading || isLoadingInvoiceParameters;
  // The first tab stands in until the period type arrives to pick the opening tab.
  const defaultTab =
    isLoadingMatterParameters || isInvoicingPeriodInUse(periodType)
      ? PreliminaryInvoiceCreationType.PERIODICAL
      : PreliminaryInvoiceCreationType.UNTIL_DATE;

  const items = useMemo(
    () => [
      {
        key: PreliminaryInvoiceCreationType.PERIODICAL,
        label: t('matter:createPreliminaryInvoiceTab.periodical'),
        children: isLoadingPeriodical ? (
          <PreliminaryInvoicePeriodicalSkeleton />
        ) : (
          <PreliminaryInvoicePeriodicalForm
            periodType={periodType}
            invoicingPeriod={invoicingPeriod}
            onConfirm={() => requestCreation({ type: PreliminaryInvoiceCreationType.PERIODICAL })}
          />
        ),
      },
      {
        key: PreliminaryInvoiceCreationType.UNTIL_DATE,
        label: t('matter:createPreliminaryInvoiceTab.untilDate'),
        children: isLoadingUntilDate ? (
          <PreliminaryInvoiceUntilDateSkeleton />
        ) : (
          <PreliminaryInvoiceUntilDateForm
            isEntryDateLocked={isEntryDateLocked}
            onConfirm={requestCreation}
          />
        ),
      },
    ],
    [
      t,
      periodType,
      invoicingPeriod,
      isEntryDateLocked,
      requestCreation,
      isLoadingPeriodical,
      isLoadingUntilDate,
    ],
  );

  return (
    <>
      <ClosableModal
        open
        title={t('matter:createPreliminaryInvoice')}
        onClose={onClose}
        className="create-preliminary-invoice-modal"
        data-testid="create-preliminary-invoice-modal"
      >
        <CreationTabs>
          <CustomTabs
            variant="pill"
            activeKey={selectedTab ?? defaultTab}
            onChange={setSelectedTab}
            items={items}
          />
        </CreationTabs>
      </ClosableModal>
      {CreationModals}
    </>
  );
};

export default CreatePreliminaryInvoiceModal;
